import { memo, useEffect, useRef, useState } from "react";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { PropertyDataV2 } from "commonModule/services/common";
import CloseIcon from "components/icons-svg/CloseIcon";
import useCurrency from "hooks/swr/useCurrency";
import { useAppSelector } from "stores/hooks";
import { selectSearch, selectShouldDisplayTotalPrice } from "stores/search/searchSlice";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import MultiUnitContent from "./info-popup-content/MultiUnitContent";
import SingleUnitContent from "./info-popup-content/SingleUnitContent";
import { autoPlace } from "./PopoverAutoPlacementLogic";
const InfoPopup = memo(({
  property,
  featuredUnitData,
  isFavourite,
  onClose,
  pageOrigin,
  setIsCloseOnOutsideEnabled,
  propertyIndex,
  isPriceMarker = false,
  hasMultiple = true,
  actualPropertyId,
  fetchPropertyCard,
  unitDataLengthRef,
  pageRef
}: {
  property: PropertyDataV2;
  featuredUnitData?: PropertyDataV2[];
  isFavourite: boolean;
  onClose: () => void;
  pageOrigin: string;
  setIsCloseOnOutsideEnabled?: (value: boolean) => void;
  propertyIndex?: number;
  isPriceMarker?: boolean;
  hasMultiple?: boolean;
  actualPropertyId?: string;
  fetchPropertyCard?: (newPage: number) => void;
  unitDataLengthRef?: React.MutableRefObject<number>;
  pageRef?: React.MutableRefObject<number>;
}) => {
  const {
    rate,
    formatter
  } = useCurrency();
  const search = useAppSelector(selectSearch);
  const [isPopoverPositioned, setIsPopoverPositioned] = useState(false);
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
  const splideRef = useRef<any | null>(null);
  const PAGE_SIZE = 10;
  const totalPages = Math.ceil((property?.multiUnitCount || 0) / PAGE_SIZE);
  const shouldDisplayTotalPrice = useAppSelector(selectShouldDisplayTotalPrice) && !!property.checkInDate && !!property.checkOutDate || !!search?.startDate && !!search.endDate;
  const totalPrice = shouldDisplayTotalPrice ? property.lowestPrice ? property.lowestPrice : property.totalBeforeTaxes || property.minPrice : property.minPrice;
  const isMultiUnitProperty = !!(property?.multiUnitGrp && property?.multiUnitCount && property?.multiUnitCount > 1 && hasMultiple);
  useEffect(() => {
    void (async () => {
      const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
      const splittedName = search.shortName?.split(",");
      mixpanelV1Client.MAP_MARKER_CLICKED({
        _id: property._id,
        propertyName: property.propertyName,
        city: splittedName?.length && splittedName?.length > 2 ? splittedName[0].trim() : "NA",
        state: splittedName?.length && splittedName?.length > 2 ? splittedName?.[1].trim() : splittedName?.length && splittedName?.length > 1 ? splittedName[0].trim() : "NA",
        country: splittedName?.length && splittedName?.length > 2 ? splittedName[2].trim() : splittedName?.length && splittedName?.length > 1 ? splittedName[1].trim() : splittedName?.[0]?.trim()
      });
    })();
  }, [property._id, property.propertyName, search.shortName]);

  // This button popoverbody is not available immediately after the component is mounted, so we are using MutationObserver to wait for the element to be available, the reason behind this is that the OverlayViewF is used instead of OverlayView. Check InnerMap.tsx for more details on why we are using OverlayViewF instead of OverlayView.
  useEffect(() => {
    let cleanup = () => {};
    setTimeout(() => {
      // This button popoverbody is not available immediately after the component is mounted, so we are using setTimeout to wait for the element to be available, the reason behind this is that the OverlayViewF is used instead of OverlayView. Check InnerMap.tsx for more details on why we are using OverlayViewF instead of OverlayView.
      const button = document.getElementById(`trigger-${actualPropertyId || property._id}`);
      const popoverBody = document.getElementById(`popoverbody-${actualPropertyId || property._id}`);
      if (!popoverBody || !button) return;
      cleanup = autoPlace(button, popoverBody);
      setIsPopoverPositioned(true);
    }, 0);
    return () => cleanup?.();
  }, [actualPropertyId, property._id]);
  return (isPopoverPositioned ? <div className='overlay-box relative mx-auto w-full max-w-[320px] rounded-lg bg-white p-3 text-start shadow-[0px_4px_20px_0px_rgba(55,_56,_77,_0.13)]'>
          <button type='button' aria-label='close popup' onClick={onClose} className='absolute left-5 top-5 z-[4] h-7 w-7 rounded-full bg-white/70 p-0 hover:bg-white'>
            <CloseIcon />
          </button>
          {isMultiUnitProperty && <div className='absolute right-5 top-5 z-[4] flex items-center gap-2 rounded-[40px] bg-[rgba(255,255,255,0.7)] px-2 text-xs font-medium leading-5 text-black-900'>
              {`${property?.multiUnitCount && property?.multiUnitCount > 0 && `${property?.multiUnitCount} ${property?.multiUnitCount > 1 ? "units" : "unit"} from`} ${totalPrice ? `${formatter(0, 0).format(totalPrice * rate)}` : "NaN"}`}
            </div>}
          {isMultiUnitProperty ? <Splide ref={splideRef} id='multi-unit-info-slider' hasTrack={false} options={{
      type: "slide",
      perPage: 1,
      padding: 0,
      drag: isLargerThan768 ? false : true,
      // drag only in mobile
      pagination: false,
      arrows: true,
      rewind: false,
      waitForTransition: true
    }} onMoved={(_, newIndex, prevIndex) => {
      if (!unitDataLengthRef || !pageRef) return;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const splide = splideRef.current?.splide;
      if (!splide) return;
      const isMovingNext = newIndex > prevIndex; // only NEXT call
      const LAST_INDEX = unitDataLengthRef.current - 1;
      const nextPage = pageRef.current + 1;
      const isLastIndex = newIndex === LAST_INDEX;
      if (isMovingNext && isLastIndex && nextPage <= totalPages && unitDataLengthRef.current < (property?.multiUnitCount || 0)) {
        fetchPropertyCard?.(nextPage);
      }

      // Disable drag ONLY on last page + last index
      if (pageRef.current === totalPages - 1 && isLastIndex) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        splide.options = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          ...splide.options,
          drag: false
        };
      } else {
        {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          splide.options = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ...splide.options,
            drag: isLargerThan768 ? false : true
          };
        }
      }
    }}>
              <SplideTrack>
                {featuredUnitData?.map((units, i) => <SplideSlide key={`units${i}`}>
                    <MultiUnitContent property={units} shouldDisplayTotalPrice={shouldDisplayTotalPrice} propertyIndex={propertyIndex} isPriceMarker={isPriceMarker} pageOrigin={pageOrigin} isMultiUnitProperty={isMultiUnitProperty} unitIndex={i + 1} actualPropertyCount={property?.multiUnitCount || 0} />
                  </SplideSlide>)}
              </SplideTrack>
            </Splide> : <SingleUnitContent property={property} shouldDisplayTotalPrice={shouldDisplayTotalPrice} propertyIndex={propertyIndex} isPriceMarker={isPriceMarker} pageOrigin={pageOrigin} hasMultiple={hasMultiple} isFavourite={isFavourite} setIsCloseOnOutsideEnabled={setIsCloseOnOutsideEnabled} />}
        </div> : null) || <></>;
});
InfoPopup.displayName = "InfoPopup";
export default InfoPopup;